import React, { useContext, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useNotificationContext, ContextForm } from "cerulean";
import { useFeature } from "byzantine";
import { useRecipient } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useLocalization } from "@fluent/react";
import Wire from "byzantine/src/Wire";
import { useShowUserNeedsApprovalAlert } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { Button, ContentCard, ResponsiveFlex } from "@narmi/design_system";
import { useIsDomesticWireDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import AccountContext from "../../../contexts/AccountContext";
import InstitutionSettingsContext from "../../../contexts/InstitutionSettingsContext";
import { useFingerPrintData } from "../../../fingerprint";
import RowItem from "./RowItem";
import DomesticWireAdditionalInfo from "../DomesticWireAdditionalInfo";
import { useSudoContext } from "../../../SudoContext";
import Container from "../../../Container";
import HeaderWithSteps from "../../../HeaderWithSteps";
import { PAYMENT_ACTIVITY_ROUTE } from "../../../PaymentActivity/routes";

const errorIsElevatedAuthError = (error) =>
  error === "Elevated authentication required to confirm this action";

const WireTransferActionReview = () => {
  const { formData: data, setOriginalRoute, requestOtpRoute } = useSudoContext();
  const { accounts } = useContext(AccountContext);
  const { wire_display_fee, wire_reason_types } = useContext(
    InstitutionSettingsContext,
  );
  const wire_reason_required = !!wire_reason_types?.length;
  const navigate = useNavigate();
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const { l10n } = useLocalization();
  const previousStep = "/transfer/wires";
  const { showAlert } = useShowUserNeedsApprovalAlert("wire");
  const { sendNotification } = useNotificationContext();
  const { featureEquals, featureEnabled } = useFeature();
  const { captureData } = useFingerPrintData("wire");
  const isDomesticWireDualApprovalRequired =
    useIsDomesticWireDualApprovalRequired();
  const recipient = useRecipient(data?.recipient);

  const goBack = () => {
    flushSync(() => {
      setOriginalRoute(previousStep);
    });
    navigate(previousStep);
  };

  const onSubmit = async (callback) => {
    // constructs a wire with the from_account, amount, and memo fields
    // and adds the recipient details
    const { recipient: recipientId, memo, ...rest } = data;
    const memoFields = Wire.splitMemo(memo);
    const wire = new Wire({ ...memoFields, ...rest }).to({
      name: recipient.name,
      ...recipient.fedwire_destination,
    });
    if (
      featureEquals("third_party_wire_transaction_evaluations", [
        "report",
        "decision",
      ])
    ) {
      const { deviceId, deviceRequestId } = await captureData();
      wire.device_id = deviceId;
      wire.device_request_id = deviceRequestId;
    }
    wire.idempotencyKey = idempotencyKey;
    wire
      .submit()
      .then((response) => {
        callback();
        const createdWire = response?.wires?.[0];
        if (createdWire?.id && featureEnabled("payment_activity")) {
          navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${createdWire.id}`);
        } else {
          navigate("/transfer");
        }
        let message = "Wire sent.";
        if (createdWire?.state === "awaiting_approval") {
          message = "Wire submitted for approval.";
        }
        sendNotification({
          type: "success",
          text: message,
        });

        if (showAlert) {
          setTimeout(() => {
            // hacky way to show the error toast after the success toast
            sendNotification({
              type: "negative",
              text: l10n.getString(
                "ach-payment-wire-approval-org-requires-users",
              ),
            });
          }, 5000);
        }
      })
      .catch((error) => {
        if (errorIsElevatedAuthError(error)) {
          callback();
          navigate(requestOtpRoute);
          return;
        }
        callback(error);
        navigate(previousStep);
      })
      .finally(() => {
        setIdempotencyKey(uuidv4());
      });
  };

  useEffect(() => {
    if (!data) {
      navigate("/transfer/wires");
    }
  }, [data]);

  if (!data) {
    return null;
  }

  const fromDisplayValue = accounts
    .find((a) => a.id === data.from_account)
    ?.getShortDescription();

  return (
    <Container>
      <ContextForm data={data}>
        <ResponsiveFlex gapSize="xl">
          <HeaderWithSteps headerText={l10n.getString("heading-wire-title")} />
          <div className="wire-transfer-review-card">
            <ContentCard kind="bordered">
              <RowItem rowName="From" rowValue={fromDisplayValue} />
              <RowItem rowName="To" rowValue={recipient?.name} />
              <RowItem rowName="Amount" rowValue={data.amount} />
              {wire_display_fee && (
                <RowItem rowName="Wire fee" rowValue={wire_display_fee} />
              )}
              {wire_reason_required && (
                <RowItem rowName="Wire Reason" rowValue={data.wire_reason} />
              )}
              {data.memo && (
                <RowItem
                  rowName={l10n.getString("label-memo")}
                  rowValue={Object.values(Wire.splitMemo(data.memo)).map(
                    (memoline) => (
                      <div key={memoline}>{memoline}</div>
                    ),
                  )}
                  isLeftAligned={true}
                />
              )}
            </ContentCard>
          </div>
          <DomesticWireAdditionalInfo />
          <div className="alignChild--center--center">
            <Button
              onClick={goBack}
              label="Back"
              kind="negative"
              type="button"
            />
            <ContextForm.Action onSubmit={onSubmit}>
              <div style={{ marginLeft: "auto" }}>
                <Button
                  label={l10n.getString(
                    isDomesticWireDualApprovalRequired
                      ? "button-submit-approval"
                      : "button-make-payment",
                  )}
                  testId="submit-wire-transfer"
                />
              </div>
            </ContextForm.Action>
          </div>
        </ResponsiveFlex>
      </ContextForm>
    </Container>
  );
};

export default WireTransferActionReview;
